import { ZION_BUILD } from '../buildInfo.ts'
import { APP_ICON_192 } from '../assets/appIcon.ts'
import { isAppleDevice } from '../ui/layout.ts'
import { isCapacitorNative } from '../api/ZionServer.ts'
import { fetchVapidPublicKey, registerPushOnServer, reportPushPresence } from '../push/pushClient.ts'
import {
  clearPushSubscriptionLocal,
  consumePushNeedsResubscribe,
  isPushEnabledLocally,
  loadPushSubscriptionEndpoint,
  markPushNeedsResubscribe,
  savePushSubscriptionJson,
  setPushEnabledLocally,
} from '../storage/pushStore.ts'
import { isStandalonePwa } from '../ui/router.ts'
import { unlockNotificationAudio } from '../notifications/audioUnlock.ts'
import {
  disableNativePush,
  enableNativePush,
  isNativePushPlatform,
  loadNativeTokenLocal,
  registerNativePushOnServer,
} from '../push/nativePush.ts'

/** Re-export payload helpers (wake / send) — title+body mai vuoti per iOS/Watch. */
export {
  normalizePushPayload,
  type PushPayload,
} from '../push/pushClient.ts'

/** Asset path usati dal SW e dalle notifiche locali (base /app/). */
export const PUSH_NOTIFY_ASSETS = {
  icon: APP_ICON_192,
  badge: `/app/icons/badge-72.png?v=${ZION_BUILD}`,
  tag: 'zion-chat-message',
  defaultTitle: 'ZION',
  defaultBody: 'Nuovo messaggio ricevuto',
} as const

export type PushSetupResult =
  | { ok: true; subscription?: PushSubscription; token?: string }
  | { ok: false; reason: string }

export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const base64Safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64Safe)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

export function isAppleMobile(): boolean {
  return isAppleDevice()
}

/** Su iPad/iPhone Web Push esiste solo nella PWA (icona Home), non in Safari.
 *  Capacitor nativo: APNs — nessuna installazione Home. */
export function iosNeedsHomeInstall(): boolean {
  if (isCapacitorNative() || isNativePushPlatform()) return false
  return isAppleDevice() && !isStandalonePwa()
}

/** iOS espone spesso pushManager solo su ServiceWorkerRegistration, non su window. */
export async function pushSupportedAsync(): Promise<boolean> {
  if (isNativePushPlatform()) return true
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false
  if ('PushManager' in window) return true
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration('/app/')) ??
      (await registerServiceWorker())
    return Boolean(reg?.pushManager)
  } catch {
    return false
  }
}

export function pushSupported(): boolean {
  if (isNativePushPlatform()) return true
  return 'serviceWorker' in navigator && 'Notification' in window
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported'
  return Notification.permission
}

/** Messaggio contestuale quando Notification.permission === 'denied'. */
export function notificationPermissionDeniedHint(): string {
  if (isCapacitorNative() || isNativePushPlatform()) {
    return 'Permesso notifiche negato. Abilitalo in Impostazioni di sistema → ZION → Notifiche.'
  }
  if (iosNeedsHomeInstall()) {
    return 'Su iPhone/iPad aggiungi ZION alla Schermata Home (Condividi → Aggiungi a Home), poi riapri l\'app dall\'icona.'
  }
  if (isStandalonePwa() && isAppleDevice()) {
    return 'Notifiche bloccate. iOS: Impostazioni → Notifiche → ZION → Consenti notifiche e banner.'
  }
  return 'Notifiche bloccate nel browser. Clicca il lucchetto (o ⋮) nella barra degli indirizzi → Impostazioni sito → Notifiche → Consenti, poi ricarica la pagina e riprova.'
}

const SW_READY_TIMEOUT_MS = 15_000

async function waitForServiceWorkerActivation(
  reg: ServiceWorkerRegistration,
  timeoutMs = SW_READY_TIMEOUT_MS,
): Promise<boolean> {
  if (reg.active) return true
  const worker = reg.installing ?? reg.waiting
  if (!worker) return false

  return new Promise<boolean>((resolve) => {
    const timer = window.setTimeout(() => resolve(Boolean(reg.active)), timeoutMs)
    worker.addEventListener('statechange', () => {
      if (reg.active) {
        window.clearTimeout(timer)
        resolve(true)
      }
    })
  })
}

async function getActiveServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  let reg = (await navigator.serviceWorker.getRegistration('/app/')) ?? null
  if (!reg) reg = await registerServiceWorker()
  if (!reg) return null

  if (reg.active) return reg

  await waitForServiceWorkerActivation(reg)
  if (reg.active) return reg

  try {
    await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => {
        window.setTimeout(() => reject(new Error('SW_READY_TIMEOUT')), SW_READY_TIMEOUT_MS)
      }),
    ])
  } catch {
    /* timeout — usa reg se attivo */
  }

  return reg.active ? reg : (await navigator.serviceWorker.getRegistration('/app/')) ?? reg
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const reg = await navigator.serviceWorker.register(`/app/sw.js?v=${ZION_BUILD}`, { scope: '/app/' })
    void reg.update().catch(() => {
      /* ignore */
    })
    return reg
  } catch (e) {
    console.error('[ZION push] Service Worker register failed:', e)
    return null
  }
}

async function getPushManager(): Promise<PushManager | null> {
  const reg = await getActiveServiceWorkerRegistration()
  return reg?.pushManager ?? null
}

export async function isVapidConfiguredOnServer(): Promise<boolean> {
  const key = await fetchVapidPublicKey()
  return Boolean(key)
}

/** Mostra banner Dashboard se push non ancora attivo (o serve PWA su iOS). */
export async function shouldShowPushActivationBanner(): Promise<boolean> {
  // Capacitor nativo: push auto all'avvio — mai banner / bottoni manuali
  if (isCapacitorNative() || isNativePushPlatform()) {
    return false
  }
  if (iosNeedsHomeInstall()) return true
  if (!(await pushSupportedAsync())) return false
  if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return false
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && isPushEnabledLocally()) {
    return false
  }
  return true
}

export function pushActivationBannerMode(): 'ios_install' | 'enable' | 'hidden' {
  if (isCapacitorNative() || isNativePushPlatform()) {
    return 'hidden'
  }
  if (iosNeedsHomeInstall()) return 'ios_install'
  if (!('Notification' in window)) return 'hidden'
  if (Notification.permission === 'denied') return 'hidden'
  if (Notification.permission === 'granted' && isPushEnabledLocally()) return 'hidden'
  return 'enable'
}

export type EnableWebPushOptions = {
  /** Avviato nel click handler (sync) — richiesto su iOS WebKit. */
  permissionPromise?: Promise<NotificationPermission>
}

/**
 * Chiamare come PRIMA istruzione nel click handler (prima di qualsiasi await).
 * Su Capacitor iOS: MAI Notification.requestPermission() (si blocca) — APNs nativo.
 * Su iOS Safari/PWA: requestPermission nel gesto utente.
 */
export function beginNotificationPermissionRequest(): Promise<NotificationPermission> {
  if (isCapacitorNative() || isNativePushPlatform()) {
    // Mai WebKit Notification API su Capacitor nativo
    return Promise.resolve('default')
  }
  if (!('Notification' in window)) return Promise.resolve('denied')
  if (Notification.permission !== 'default') return Promise.resolve(Notification.permission)
  try {
    return Notification.requestPermission()
  } catch {
    return Promise.resolve('denied')
  }
}

async function resolveNotificationPermission(
  permissionPromise?: Promise<NotificationPermission>,
): Promise<NotificationPermission> {
  // Capacitor: permesso gestito SOLO da PushNotifications.requestPermissions()
  if (isCapacitorNative() || isNativePushPlatform()) {
    return 'default'
  }
  if (permissionPromise) return permissionPromise
  if (!('Notification' in window)) return 'denied'
  if (Notification.permission !== 'default') return Notification.permission
  return Notification.requestPermission()
}

/**
 * Richiesta permessi + subscribe VAPID + salvataggio sul backend.
 * Deve essere invocata solo da click/tap utente (iOS).
 */
export async function requestPushPermissionAndSubscribe(
  sessionToken: string,
  options: EnableWebPushOptions = {},
): Promise<PushSetupResult> {
  return enableWebPush(sessionToken, options)
}

export async function enableWebPush(
  sessionToken: string,
  options: EnableWebPushOptions = {},
): Promise<PushSetupResult> {
  const session = String(sessionToken ?? '').trim()
  if (!session) {
    return { ok: false, reason: 'Sessione non valida. Sblocca di nuovo l\'app e riprova.' }
  }

  // Capacitor iOS/Android → push nativo (niente Web Push / Notification.requestPermission)
  if (isCapacitorNative() || isNativePushPlatform()) {
    const native = await enableNativePush(session)
    if (!native.ok) return native
    return { ok: true, token: native.token }
  }

  if (iosNeedsHomeInstall()) {
    return {
      ok: false,
      reason:
        'Su iPad/iPhone le notifiche push richiedono di aggiungere ZION alla Home (Condividi → Aggiungi a Schermata Home), poi aprire l\'app dall\'icona.',
    }
  }

  if (!('Notification' in window)) {
    return { ok: false, reason: 'Notifiche non supportate su questo browser.' }
  }

  try {
    const permission = await resolveNotificationPermission(options.permissionPromise)
    if (permission !== 'granted') {
      return {
        ok: false,
        reason:
          permission === 'denied'
            ? notificationPermissionDeniedHint()
            : 'Permesso notifiche non concesso.',
      }
    }

    void unlockNotificationAudio()

    if (!(await pushSupportedAsync())) {
      const mode = isStandalonePwa() ? 'PWA Home' : 'Safari'
      return {
        ok: false,
        reason: `Web Push non disponibile (${mode}). iPad: iOS 16.4+ e app in Home.`,
      }
    }

    const reg = await getActiveServiceWorkerRegistration()
    if (!reg) {
      return {
        ok: false,
        reason: 'Service Worker non registrato. Ricarica la pagina o riapri l\'app dalla Home.',
      }
    }

    const vapidPublic = await fetchVapidPublicKey()
    if (!vapidPublic) {
      return {
        ok: false,
        reason: 'Push non raggiungibile (VAPID). Verifica connessione e riprova.',
      }
    }

    const pushManager = reg.pushManager ?? (await getPushManager())
    if (!pushManager) {
      return { ok: false, reason: 'PushManager non disponibile su questo dispositivo.' }
    }

    // iOS PWA: unsubscribe SEMPRE la subscription precedente prima di crearne una nuova
    // (endpoint obsolete / Safari vs Standalone).
    try {
      const existingSub = await pushManager.getSubscription()
      if (existingSub) {
        console.log('[ZION push] unsubscribe existing before resubscribe')
        await existingSub.unsubscribe()
      }
    } catch (e) {
      console.warn('[ZION push] unsubscribe existing failed (continuo):', e)
    }

    let subscription: PushSubscription
    try {
      subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublic) as BufferSource,
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      return { ok: false, reason: `Sottoscrizione push fallita: ${msg}` }
    }

    const ok = await registerPushOnServer(session, subscription)
    if (!ok) {
      return { ok: false, reason: 'Registrazione sul server fallita.' }
    }

    savePushSubscriptionJson(JSON.stringify(subscription.toJSON()))
    setPushEnabledLocally(true)
    console.log('[ZION push] subscribed OK', subscription.endpoint?.slice(0, 64))
    return { ok: true, subscription }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    return { ok: false, reason: `Errore attivazione push: ${msg}` }
  }
}

/** Scadenza entro 48h (o già scaduta) → forza nuova subscribe. */
function subscriptionExpiringSoon(sub: PushSubscription | null): boolean {
  if (!sub) return false
  const exp = (sub as PushSubscription & { expirationTime?: number | null }).expirationTime
  if (exp == null || !Number.isFinite(exp) || exp <= 0) return false
  return exp - Date.now() < 48 * 60 * 60 * 1000
}

/**
 * Risincronizza l'abbonamento WebPush col server.
 * - Se il token/endpoint è cambiato o scade a breve → re-subscribe + register
 * - Se manca la subscription ma era attiva (o 410 needs-resubscribe) → nuova subscribe
 * Chiamare all'avvio e su visibilitychange/focus/pageshow.
 */
export async function syncPushRegistration(sessionToken: string): Promise<void> {
  const session = String(sessionToken ?? '').trim()
  if (!session) return

  if (isNativePushPlatform()) {
    const token = loadNativeTokenLocal()
    if (token && isPushEnabledLocally()) {
      void registerNativePushOnServer(session, token)
    }
    return
  }

  if (iosNeedsHomeInstall()) return
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  if (!(await pushSupportedAsync())) return

  const reg = await getActiveServiceWorkerRegistration()
  const pushManager = reg?.pushManager
  if (!pushManager) return

  const forceResubFlag = consumePushNeedsResubscribe()
  let subscription = await pushManager.getSubscription()
  const previousEndpoint = loadPushSubscriptionEndpoint()
  const expiring = subscriptionExpiringSoon(subscription)
  const forceResub = forceResubFlag || expiring

  // Subscription sparita / scadente / 410 → ricrea con VAPID e POST immediato
  if (!subscription || forceResub) {
    if (!isPushEnabledLocally() && !forceResub && !previousEndpoint) return
    if (subscription) {
      try {
        await subscription.unsubscribe()
      } catch {
        /* ignore */
      }
      subscription = null
    }
    const vapidPublic = await fetchVapidPublicKey()
    if (!vapidPublic) return
    try {
      subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublic) as BufferSource,
      })
      console.log(
        '[ZION push] resubscribed',
        forceResubFlag ? '(410/gone)' : expiring ? '(expiring)' : '(missing)',
      )
    } catch (e) {
      console.warn('[ZION push] resubscribe failed:', e)
      return
    }
  }

  if (!subscription) return

  const endpoint = String(subscription.endpoint || '').trim()
  const endpointChanged = Boolean(previousEndpoint && endpoint && previousEndpoint !== endpoint)

  // Sempre re-POST register: anche stesso endpoint rinnova TTL server.
  const ok = await registerPushOnServer(session, subscription)
  if (!ok) {
    console.warn('[ZION push] register sync failed — will retry on next focus')
    markPushNeedsResubscribe()
    return
  }

  savePushSubscriptionJson(JSON.stringify(subscription.toJSON()))
  setPushEnabledLocally(true)
  if (endpointChanged) {
    console.log('[ZION push] endpoint changed — server updated', endpoint.slice(0, 64))
  }
}

/** Alias esplicito per lifecycle (focus / unlock / visibility). */
export async function ensurePushTokenSynced(sessionToken: string): Promise<void> {
  try {
    await syncPushRegistration(sessionToken)
  } catch (e) {
    console.warn('[ZION push] ensurePushTokenSynced failed:', e)
  }
}

/**
 * All'avvio PWA: se permesso già concesso, registra/sincronizza push senza passare da Impostazioni.
 */
export async function ensurePwaPushOnLaunch(sessionToken: string): Promise<void> {
  if (isCapacitorNative() || isNativePushPlatform()) return
  if (iosNeedsHomeInstall()) return
  const session = String(sessionToken ?? '').trim()
  if (!session) return
  if (!('Notification' in window)) return

  if (Notification.permission === 'denied') return

  if (Notification.permission === 'granted') {
    if (!isPushEnabledLocally()) {
      const result = await enableWebPush(session)
      if (!result.ok) {
        console.warn('[ZION push] auto-enable on launch:', result.reason)
      }
      return
    }
    await ensurePushTokenSynced(session)
    return
  }

  // Permesso mai chiesto: sync silenziosa se subscription già presente (reinstall / update)
  await ensurePushTokenSynced(session)
}

let presenceSession = ''
let presenceHooksInstalled = false
let pushLifecycleHooksInstalled = false
let lastPushSyncAt = 0
let lastHiddenAt = 0
let pushKeepAliveTimer: ReturnType<typeof setInterval> | null = null
const PUSH_SYNC_MIN_INTERVAL_MS = 8_000
/** Dopo inattività lunga (≥2 min hidden) bypassa il debounce al rientro. */
const PUSH_SYNC_LONG_HIDDEN_MS = 2 * 60_000
/** Keep-alive subscription mentre la PWA resta aperta. */
const PUSH_KEEPALIVE_MS = 20 * 60_000

function flushPushPresence(visible: boolean): void {
  const sess = presenceSession.trim()
  if (!sess) return
  void reportPushPresence(sess, visible)
}

function currentDocumentVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
}

export function startPushPresenceHeartbeat(sessionToken: string): void {
  // Capacitor nativo: MAI segnalare "visible" — altrimenti il server salta APNs
  // e iOS non può mostrare il banner di sistema ad app aperta.
  if (isCapacitorNative() || isNativePushPlatform()) {
    presenceSession = ''
    return
  }
  const sess = sessionToken.trim()
  if (!sess) return
  presenceSession = sess
  flushPushPresence(currentDocumentVisible())

  if (presenceHooksInstalled) return
  presenceHooksInstalled = true

  document.addEventListener('visibilitychange', () => {
    flushPushPresence(document.visibilityState === 'visible')
  })
  window.addEventListener('focus', () => flushPushPresence(true))
  window.addEventListener('blur', () => {
    if (document.visibilityState !== 'visible') flushPushPresence(false)
  })
  window.addEventListener('pagehide', () => flushPushPresence(false))
}

export function stopPushPresenceHeartbeat(): void {
  if (presenceSession) flushPushPresence(false)
  presenceSession = ''
}

/**
 * All'apertura / sblocco / focus della PWA: risincronizza push token col server.
 * Dedup leggero per non martellare /register su ogni focus rapido;
 * dopo inattività lunga forza sempre il check subscription + POST endpoint.
 */
export function installPushLifecycleHooks(getSessionToken: () => Promise<string | null>): void {
  if (pushLifecycleHooksInstalled) return
  pushLifecycleHooksInstalled = true

  const run = (opts?: { force?: boolean }) => {
    const now = Date.now()
    const longHidden =
      lastHiddenAt > 0 && now - lastHiddenAt >= PUSH_SYNC_LONG_HIDDEN_MS
    const force = Boolean(opts?.force) || longHidden
    if (!force && now - lastPushSyncAt < PUSH_SYNC_MIN_INTERVAL_MS) return
    lastPushSyncAt = now
    if (force) lastHiddenAt = 0
    void getSessionToken().then((t) => {
      const sess = String(t || '').trim()
      if (!sess) return
      void ensurePushTokenSynced(sess)
    })
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      lastHiddenAt = Date.now()
      return
    }
    if (document.visibilityState === 'visible') run({ force: false })
  })
  window.addEventListener('pageshow', () => run({ force: true }))
  window.addEventListener('focus', () => run({ force: false }))

  // Keep-alive: mentre la PWA è visibile, rinnova periodicamente endpoint sul server
  if (pushKeepAliveTimer == null) {
    pushKeepAliveTimer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      run({ force: true })
    }, PUSH_KEEPALIVE_MS)
  }

  // SW: pushsubscriptionchange → forza resync
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const type = event.data?.type
      if (type === 'ZION_PUSH_SUBSCRIPTION_CHANGED' || type === 'ZION_PUSH_NEEDS_RESYNC') {
        markPushNeedsResubscribe()
        lastPushSyncAt = 0
        run({ force: true })
      }
    })
  }
}

export async function disableWebPush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  try {
    if (isNativePushPlatform()) {
      await disableNativePush()
      clearPushSubscriptionLocal()
      return { ok: true }
    }
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration('/app/')
      const sub = await reg?.pushManager?.getSubscription()
      if (sub) await sub.unsubscribe()
    }
    clearPushSubscriptionLocal()
    return { ok: true }
  } catch (e) {
    clearPushSubscriptionLocal()
    const msg = e instanceof Error ? e.message : String(e)
    return { ok: false, reason: `Disattivazione fallita: ${msg}` }
  }
}

/**
 * Reset completo: unsubscribe + nuova subscription + re-register sul server.
 * Da usare da Impostazioni su iOS quando le push non arrivano.
 */
export async function resetAndReenableWebPush(
  sessionToken: string,
  options: EnableWebPushOptions = {},
): Promise<PushSetupResult> {
  console.log('[ZION push] Reset & Riattiva — inizio')
  await disableWebPush()
  clearPushSubscriptionLocal()
  if (!isNativePushPlatform()) {
    try {
      const reg = await getActiveServiceWorkerRegistration()
      await reg?.update()
    } catch {
      /* ignore */
    }
  }
  const result = await enableWebPush(sessionToken, options)
  console.log('[ZION push] Reset & Riattiva — esito', result.ok ? 'OK' : result.reason)
  return result
}

export function pushModeLabel(): string {
  if (isNativePushPlatform() || isCapacitorNative()) return 'App nativa (APNs)'
  if (isStandalonePwa()) return 'PWA (icona Home)'
  if (isAppleDevice()) return 'Safari — serve icona Home'
  return 'Browser'
}

export async function pushStatusLabelAsync(): Promise<string> {
  const mode = pushModeLabel()
  if (isNativePushPlatform() || isCapacitorNative()) {
    if (isPushEnabledLocally() && loadNativeTokenLocal()) {
      return `Notifiche native APNs attive (${mode}).`
    }
    if (isPushEnabledLocally()) return `Notifiche native in corso (${mode}).`
    return `Pronto — tocca Attiva notifiche (${mode}).`
  }
  if (iosNeedsHomeInstall()) {
    return `${mode}: aggiungi ZION a Home per le notifiche push.`
  }
  if (!(await pushSupportedAsync())) {
    return `Web Push non disponibile (${mode}).`
  }
  const vapidOk = await isVapidConfiguredOnServer()
  if (!vapidOk) {
    if (isStandalonePwa()) {
      return 'VAPID non attivo sul server. Contatta l\'admin o riprova più tardi.'
    }
    return 'Push server non raggiungibile (VAPID). Riprova tra poco o controlla la connessione.'
  }
  const perm = Notification.permission
  if (perm === 'denied') return notificationPermissionDeniedHint()
  if (perm === 'granted' && isPushEnabledLocally()) return `Notifiche push attive (${mode}).`
  if (perm === 'default') return `Pronto — tocca Attiva notifiche (${mode}).`
  return 'Stato sconosciuto.'
}
